import { gql } from '@apollo/client';

export const CREATE_GROUP = gql`
  mutation GROUP_SERVICE_createGroup($inputGroup: InputGroup) {
    createGroup(group: $inputGroup) {
      id
      name
      agentOfRecord {
        id
        name
        npn
        type
        email
      }
      accountRepresentative {
        id
        firstName
        lastName
        email
      }
      assigned {
        id
        name
        npn
        type
      }
      contactInfo {
        firstName
        lastName
        primaryEmail
        title
        primaryPhone
        primaryAddress {
          city
          street
          street2
          zip
          state
          county
        }
      }
      notes
    }
  }
`;

export const UPDATE_GROUP = gql`
  mutation GROUP_SERVICE_updateGroup($inputGroup: InputGroup) {
    updateGroup(group: $inputGroup) {
      id
      name
      lastUpdateTime
      agentOfRecord {
        id
        name
        npn
        type
        email
      }
      accountRepresentative {
        id
        firstName
        lastName
        email
      }
      assigned {
        id
        name
        npn
        type
      }
      contactInfo {
        firstName
        lastName
        primaryEmail
        title
        primaryPhone
        primaryAddress {
          city
          street
          street2
          zip
          state
          county
        }
      }
      notes
      archived
    }
  }
`;

export const GROUP_BY_ID = gql`
  query GROUP_SERVICE_groupById($id: ID!) {
    groupById(id: $id) {
      id
      name
      lastUpdateTime
      source
      creationDateTime
      carrierGroupNumber
      agentOfRecord {
        id
        name
        npn
        type
        email
        tin
      }
      accountRepresentative {
        id
        firstName
        lastName
        email
      }
      assigned {
        id
        name
        type
        npn
      }
      contactInfo {
        firstName
        lastName
        primaryEmail
        title
        primaryPhone
        primaryAddress {
          city
          street
          street2
          zip
          state
          county
        }
      }
      notes
      archived
    }
  }
`;

export const GET_GROUPS_PAGE = gql`
  query GROUP_SERVICE_getGroupsPage(
    $sortBy: SortBy = UPDATE_TIME
    $sortOrder: SortOrder = DESC
    $filter: Filter = ALL
    $page: Int = 1
    $fullView: Boolean = false
  ) {
    getGroupsPage(sortBy: $sortBy, sortOrder: $sortOrder, filter: $filter, page: $page, fullView: $fullView) {
      result {
        id
        name
        lastUpdateTime
        source
        creationDateTime
        archived
        agentOfRecord {
          id
          name
          npn
          type
          email
        }
        accountRepresentative {
          id
          firstName
          lastName
          email
        }
        assigned {
          id
          name
          npn
          type
        }
      }
      totalElements
      currentPage
      totalPages
    }
  }
`;

export const SAVE_NOTES = gql`
  mutation GROUP_SERVICE_saveNotes($groupId: ID!, $notes: String!) {
    saveNotes(groupId: $groupId, notes: $notes) {
      id
      notes
    }
  }
`;

export const GET_ASSIGNEES_AND_AGENTS_OF_RECORDS = gql`
  query GROUP_SERVICE_getAssignees($agencyId: ID) {
    getAssignees(agencyId: $agencyId) {
      id
      name
      npn
      type
    }
    getAgentsOfRecord(agencyId: $agencyId) {
      id
      name
      npn
      type
    }
  }
`;

export const GET_AGENTS_OF_RECORD = gql`
  query GROUP_SERVICE_getAgentsOfRecord {
    getAgentsOfRecord {
      id
      name
      npn
      type
    }
  }
`;

export const UPDATE_AGENT_OF_RECORD = gql`
  mutation GROUP_SERVICE_updateAgentOfRecord($groupId: ID!, $agentId: ID!) {
    updateAgentOfRecord(groupId: $groupId, agentId: $agentId) {
      id
      agentOfRecord {
        id
        name
        npn
        type
        email
      }
    }
  }
`;

export const SEARCH_GROUP_BY_NAME = gql`
  query GROUP_SERVICE_searchGroupByName($groupName: String!, $limit: Int = 10, $fullView: Boolean = false) {
    searchGroupByName(groupName: $groupName, limit: $limit, fullView: $fullView) {
      id
      name
      canAccess
    }
  }
`;

export const ARCHIVE_GROUP = gql`
  mutation GROUP_SERVICE_archiveGroup($groupId: ID!) {
    archiveGroup(groupId: $groupId) {
      id
    }
  }
`;

export const UNARCHIVE_GROUP = gql`
  mutation GROUP_SERVICE_unarchiveGroup($groupId: ID!) {
    unarchiveGroup(groupId: $groupId) {
      id
    }
  }
`;

export const GET_GROUPS_BY_USER_ID = gql`
  query GROUP_SERVICE_getGroupsByUserId {
    getGroupsByUserId {
      id
      name
      source
      contactInfo {
        firstName
        lastName
        primaryEmail
        title
        primaryPhone
        primaryAddress {
          city
          street
          street2
          zip
          state
          county
        }
      }
      creationDateTime
      lastUpdateTime
      agentOfRecord {
        id
        name
        npn
        type
        email
      }
    }
  }
`;

export const GET_SPONSORS_GROUPS_PAGE = gql`
  query GROUP_SERVICE_getSponsorGroupsPage(
    $filter: Filter = ALL
    $name: String = ""
    $page: Int = 1
    $sponsorId: String
    $agencyId: String = ""
    $sortBy: SortBy = UPDATE_TIME
    $sortOrder: SortOrder = DESC
    $effectiveDate: String = ""
    $assignedGroups: Boolean = false
    $createdBy: Boolean = false
    $state: String = ""
    $carrierGroupNumber: String = ""
  ) {
    getSponsorGroupsPage(
      filter: $filter
      name: $name
      page: $page
      sponsorId: $sponsorId
      agencyId: $agencyId
      sortBy: $sortBy
      sortOrder: $sortOrder
      effectiveDate: $effectiveDate
      assignedGroups: $assignedGroups
      createdBy: $createdBy
      state: $state
      carrierGroupNumber: $carrierGroupNumber
    ) {
      result {
        id
        name
        carrierGroupNumber
        contactInfo {
          primaryAddress {
            state
          }
        }
        lastUpdateTime
        source
        creationDateTime
        agentOfRecord {
          id
          name
          npn
          type
          email
        }
        accountRepresentative {
          id
          firstName
          lastName
          email
        }
        assigned {
          id
          name
          type
          npn
        }
        archived
        agency {
          id
          name
        }
        archived
      }
      totalElements
      currentPage
      totalPages
    }
  }
`;

export const GET_RENEWAL_GROUPS_PAGE = gql`
  query GROUP_SERVICE_V2_getRenewalGroupsPage(
    $effectiveDate: String
    $sponsorId: String
    $name: String = ""
    $page: Int = 1
    $filter: Filter = INSTALLED
  ) {
    getRenewalGroupsPage(
      effectiveDate: $effectiveDate
      sponsorId: $sponsorId
      name: $name
      page: $page
      filter: $filter
    ) {
      result {
        id
        name
        source
        browsingCriteria
        contactInfo {
          firstName
          lastName
          primaryPhone
          primaryEmail
          primaryAddress {
            street
            street2
            zip
            state
            county
            city
          }
          title
        }
        creationDateTime
        lastUpdateTime
        agentOfRecord {
          id
          name
          type
          npn
          email
          firstName
          lastName
        }
        accountRepresentative {
          id
          firstName
          lastName
          email
        }
        assigned {
          id
          name
          type
          npn
        }
        notes
        archived
        agency {
          id
          name
        }
        additionalAORs {
          id
          name
          firstName
          lastName
          type
          npn
          email
        }
        additionalContactInfos {
          firstName
          lastName
          primaryPhone
          primaryEmail
          primaryAddress {
            street
            street2
            zip
            state
            county
            city
          }
          title
        }
      }
      currentPage
      totalPages
      totalElements
    }
  }
`;

export const SEND_RENEWAL_DIGEST = gql`
  mutation GROUP_SERVICE_V2_sendRenewalDigest($sponsorId: String, $renewalEffectiveDate: String) {
    sendRenewalDigest(sponsorId: $sponsorId, renewalEffectiveDate: $renewalEffectiveDate)
  }
`;

export const REMOVE_GROUP = gql`
  mutation GROUP_SERVICE_V2_removeGroup($groupId: ID!) {
    removeGroup(groupId: $groupId)
  }
`;

export const TRANSFER_GROUP = gql`
  mutation GROUP_SERVICE_V2_transferGroup($groupId: ID!, $agencyId: ID!, $agentNpn: String, $agentTin: String) {
    transferGroup(groupId: $groupId, agencyId: $agencyId, agentNpn: $agentNpn, agentTin: $agentTin)
  }
`;

export const UPDATE_RESPONSIBLE_PARTIES = gql`
  mutation GROUP_SERVICE_updateResponsibleParties(
    $groupId: ID!
    $agentId: ID!
    $assignees: [ID]!
    $newAccRep: InputAccountRepresentative!
  ) {
    updateAgentOfRecord(groupId: $groupId, agentId: $agentId) {
      id
      agentOfRecord {
        id
        name
        npn
        type
        email
      }
    }
    saveAssignee(groupId: $groupId, assignees: $assignees) {
      id
      assigned {
        id
        name
        type
        npn
      }
    }
    updateAccountRepresentative(groupId: $groupId, newAccRep: $newAccRep) {
      id
      accountRepresentative {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

export const UPDATE_AGENT_OF_RECORD_AND_ASSIGNEES = gql`
  mutation GROUP_SERVICE_updateAgentOfRecordAndAssignees($groupId: ID!, $agentId: ID!, $assignees: [ID]!) {
    updateAgentOfRecord(groupId: $groupId, agentId: $agentId) {
      id
      agentOfRecord {
        id
        name
        npn
        type
        email
      }
    }
    saveAssignee(groupId: $groupId, assignees: $assignees) {
      id
      assigned {
        id
        name
        type
        npn
      }
    }
  }
`;
